/* eslint-disable no-console */
import { ISessionContext } from "@jupyterlab/apputils";
import { Cell, CodeCell, CodeCellModel, ICellModel } from "@jupyterlab/cells";
import { CodeMirrorEditor } from "@jupyterlab/codemirror";
import { CellType } from "@jupyterlab/nbformat";
import { Notebook, NotebookActions } from "@jupyterlab/notebook";
import { ObservableUndoableList } from "@jupyterlab/observables";
import { Signal } from "@lumino/signaling";
import Postmate from "postmate";
import { v4 as uuid } from "uuid";
import {
  CLASS_NAME_COLLAPSED,
  CLASS_NAME_COLLAPSED_OUTPUT,
} from "../../components/jupyter/JupyterLabGlobalStyle";
import { JupyterLabGlobal } from "../../pages/editor/utils/getJupyterLab";
import { CellInfo } from "../../types/CellInfo";
import { CodeLocation } from "../../types/CodeLocation";
import { JupyterEvent, NotebookClient } from "../types";
import { VerticalDirection } from "./enums";

export enum KaggleCellMetadataKey {
  KAGGLE_FREEZE = "KAGGLE_FREEZE",
}

export interface JupyterLabNotebookClient extends NotebookClient {
  onCellStateChange: (onchange: (cells: CellInfo[]) => void) => void;
  runCommand: (cmd: string, index: number | null) => void;
  onCellQueueChange: (onchange: () => void) => void;
  isQueued: (cellid: string) => boolean;
  clearCellQueue: () => void;
  getSourceWithOutputs: () => string;
  logAnalyticsEvent: (eventType: string, data?: any) => void;
}

// List of Notebook Command strings:
// https://github.com/jupyterlab/jupyterlab/blob/ae08605b296e45facb7da138414900f539b114a0/packages/notebook-extension/src/index.ts#L100

/** Creates an instance of a NotebookClient. */
export const createJupyterLabNotebookClient = (
  jl: JupyterLabGlobal,
  pm: Postmate.ChildAPI
): JupyterLabNotebookClient => {
  const runQueue: (() => void)[] = [];
  let queuedCells: any = {};
  let isConnected = false;
  let onQueueChange = () => {};
  function onCellQueueChange(onchange: () => void) {
    onQueueChange = onchange;
  }
  const triggerRun = () => pm.emit(JupyterEvent.CODE_CELL_EXECUTE);
  const logAnalyticsEvent = (eventType: string, data?: any) =>
    pm.emit("kernelsAnalyticsEvent", { eventType, data });

  const saveState = (cell: Cell) => {
    const o = cell.model.value.text;
    return (newCell: Cell) => {
      newCell.model.value.text = o;
    };
  };

  const runOffline = (fn: () => void) => {
    if (isConnected) {
      return fn();
    }
    triggerRun();
    runQueue.push(fn);
    onQueueChange();
    return;
  };

  const runCellsOffline = (
    cells: number[],
    captureState: (cell: Cell) => (cell: Cell) => void = saveState
  ) => {
    if (isConnected) {
      return;
    }
    triggerRun();
    cells.forEach(c => {
      withNotebook(nb => {
        const cell = nb.widgets[c];
        if (!cell.id) {
          cell.id = uuid();
        }
        const id = cell.id;
        if (queuedCells[id]) {
          return;
        }
        queuedCells[cell.id] = true;
        onQueueChange();
        const restore = captureState(cell);
        runQueue.push(() => {
          nb.widgets
            .map((ci, i) => ({ cid: ci.id, i }))
            .filter(cx => cx.cid === id)
            .forEach(async rc => {
              const rcell = nb.widgets[rc.i];
              const restoreNew = saveState(rcell);
              restore(rcell);
              // We don't await this command so that users don't notice the momentary cell code swap between restore/restoreNew.
              runCommand("notebook:run-cell", rc.i);
              restoreNew(rcell);
            });
        });
      });
    });
  };

  function isQueued(cellid: string) {
    return queuedCells[cellid];
  }

  function clearCellQueue() {
    queuedCells = {};
    while (runQueue.length > 0) {
      runQueue.shift();
    }
    onQueueChange();
  }

  function range(start: number, end: number) {
    if (end - start <= 0) {
      return [];
    }
    return Array(end - start)
      .fill(0)
      .map((_, i) => i + start);
  }

  function currentCellIndex() {
    return (
      withNotebook(nb => {
        return nb.activeCellIndex;
      }) || 0
    );
  }

  function numCells() {
    return (
      withNotebook(nb => {
        return nb.widgets.length;
      }) || 0
    );
  }

  withNotebook((_, ctx) => {
    switch (ctx.kernelDisplayStatus) {
      case "idle":
      case "busy":
        isConnected = true;
        break;
      default:
        isConnected = false;
    }

    ctx.statusChanged.connect((_slot, status) => {
      switch (status) {
        case "idle":
        case "busy":
          queuedCells = {};
          onQueueChange();
          while (runQueue.length > 0) {
            const fn = runQueue.shift();
            if (fn) {
              fn();
            }
          }
          isConnected = true;
          break;
        default:
          isConnected = false;
      }
    });
  });

  // withNotebook executes the given function if the notebook and session context
  // are available.
  // TODO(herbison): Is SessionContext available before kernel is ready?
  function withNotebook<T>(fn: (nb: Notebook, ctx: ISessionContext) => T) {
    const nb = jl.notebookTracker.currentWidget?.content;
    const ctx = jl.context?.sessionContext;
    if (!nb || !ctx) {
      return null;
    }
    return fn(nb, ctx);
  }

  /**
   * Executes the passed function on the notebook, temporarily setting the active cell
   * to the one at the given index. If index is null, it uses the already active cell.
   */
  function withActive<T>(
    index: number | null,
    fn: (nb: Notebook, ctx: ISessionContext) => T
  ) {
    return withNotebook((nb, ctx) => {
      if (index == null) {
        return fn(nb, ctx);
      }
      // Remember original active cell, and active the passed one:
      const originalIndex = nb.activeCellIndex;
      nb.activeCellIndex = index;

      const result = fn(nb, ctx);

      // Running the fn may have moved our active cell, so we should adjust things so we reactivate the original cell.
      const shift = nb.activeCellIndex - index;
      nb.activeCellIndex = originalIndex + shift;

      return result;
    });
  }

  function runCommand(cmd: string, index: number | null = null) {
    return withActive(index, () => {
      return jl.app.commands.execute(cmd);
    });
  }

  // executeInCell returns a function which can be passed to withActive or withNotebook.
  // It executes the code returned by the function passed to this method inside the active cell.
  function executeInCell(fn: (cell: CodeCell) => string) {
    return (nb: Notebook, ctx: ISessionContext) => {
      const cell = nb.activeCell as CodeCell;
      if (!cell) {
        return;
      }
      // WARNING: This is a bit hacky because there doesn't seem to be
      // a simple way to execute just the selected code in a cell.

      // Record the original value of the cell so it can be restored later.
      const ovalue = cell.model.value.text;

      // Momentarily replace the cell content with the passed code
      // and then execute the cell to execute just the passed code.
      cell.model.value.text = fn(cell);
      CodeCell.execute(cell, ctx);

      // Restore orginal value of the cell.
      cell.model.value.text = ovalue;
    };
  }

  // Hide unwanted commands from the right-click menu:
  function hideCommand(cmd: string) {
    const command = (jl.app.commands as any)._commands[cmd];
    if (command) {
      command.isVisible = () => false;
    }
  }
  hideCommand("logconsole:open");
  hideCommand("inspector:open");
  hideCommand("notebook:create-console");
  hideCommand("notebook:create-output-view");
  hideCommand("lsp:show-diagnostics-panel-notebook");

  jl.app.commands.addCommand("notebook:queue-run-cell", {
    execute: () => {
      const index = currentCellIndex();
      runCellsOffline([index]);
      return runCommand("notebook:run-cell", index);
    },
  });

  jl.app.commands.addCommand("notebook:queue-run-cell-and-insert-below", {
    execute: () => {
      runCellsOffline([currentCellIndex()]);
      return runCommand("notebook:run-cell-and-insert-below");
    },
  });

  jl.app.commands.addCommand("notebook:queue-run-cell-and-select-next", {
    execute: () => {
      runCellsOffline([currentCellIndex()]);
      return runCommand("notebook:run-cell-and-select-next");
    },
  });

  jl.app.commands.addCommand("notebook:run-all-cells-stay-editing", {
    execute: () => {
      withNotebook(nb => {
        const i = nb.activeCellIndex;
        runCellsOffline(range(0, numCells()));
        runCommand("notebook:run-all-cells");
        nb.activeCellIndex = i;
        nb.mode = "edit";
      });
    },
  });

  const getSelectedCodeOrLine = (cell: Cell) => {
    const editor = cell.editor as CodeMirrorEditor;
    const selected = (editor.doc.getSelection() as string | null) ?? "";
    if (selected !== "") {
      return selected;
    }
    const line = editor.getCursorPosition().line;
    return editor.getLine(line) ?? "";
  };

  jl.app.commands.addCommand("notebook:execute-selected-code", {
    execute: () => {
      runCellsOffline([currentCellIndex()], (cell: Cell) => {
        const selected = getSelectedCodeOrLine(cell);
        return (newCell: Cell) => {
          newCell.model.value.text = selected;
        };
      });
      withNotebook(executeInCell(getSelectedCodeOrLine));
    },
  });

  const getSelectedCode = (cell: Cell) => {
    const editor = cell.editor as CodeMirrorEditor;
    return editor.doc.getSelection();
  };

  jl.app.commands.addCommand("notebook:execute-active-selection", {
    execute: () => {
      runCellsOffline([currentCellIndex()], (cell: Cell) => {
        const selected = getSelectedCode(cell);
        return (newCell: Cell) => {
          newCell.model.value.text = selected;
        };
      });
      withNotebook(executeInCell(getSelectedCode));
    },
  });

  function setCellClass(cell: Cell, cls: string, shouldHave: boolean) {
    if (shouldHave) {
      cell.addClass(cls);
    } else {
      cell.removeClass(cls);
    }
  }

  const cellHiddenSignal = new Signal(jl);

  function setActiveCellMetadata(key: string, isHidden: boolean) {
    withNotebook(nb => {
      nb.widgets
        .filter(cell => nb.isSelectedOrActive(cell))
        .forEach(cell => cell.model.metadata.set(key, isHidden));
      cellHiddenSignal.emit(key);
    });
  }

  // Normalize the CSS classes to match the notebook metadata:
  const normalizeClasses = () => {
    withNotebook(nb => {
      nb.widgets.forEach(cell => {
        setCellClass(
          cell,
          CLASS_NAME_COLLAPSED,
          !!cell.model.metadata.get(CLASS_NAME_COLLAPSED) || false
        );
        setCellClass(
          cell,
          CLASS_NAME_COLLAPSED_OUTPUT,
          !!cell.model.metadata.get(CLASS_NAME_COLLAPSED_OUTPUT) || false
        );
      });
    });
  };
  normalizeClasses();

  const clearReadOnly = () => {
    withNotebook(nb => {
      const model = nb.model;
      if (!model) {
        return;
      }
      if (model.readOnly) {
        model.readOnly = false;
      }
      nb?.widgets.forEach(cell => {
        if (cell.readOnly) {
          cell.readOnly = false;
        }
      });
    });
  };

  // Add JupyterLab commands for hiding/showing Notebook cells & output cells in the Notebook Viewer.
  jl.app.commands.addCommand("notebook:hide-cell-in-viewer", {
    label: "Hide Cell in Viewer",
    execute: () => setActiveCellMetadata(CLASS_NAME_COLLAPSED, true),
  });
  jl.app.commands.addCommand("notebook:show-cell-in-viewer", {
    label: "Show Cell in Viewer",
    execute: () => setActiveCellMetadata(CLASS_NAME_COLLAPSED, false),
  });
  jl.app.commands.addCommand("notebook:hide-output-cell-in-viewer", {
    label: "Hide Output Cell in Viewer",
    execute: () => setActiveCellMetadata(CLASS_NAME_COLLAPSED_OUTPUT, true),
  });
  jl.app.commands.addCommand("notebook:show-output-cell-in-viewer", {
    label: "Show Output Cell in Viewer",
    execute: () => setActiveCellMetadata(CLASS_NAME_COLLAPSED_OUTPUT, false),
  });

  // Add the hide/show Notebook Viewer cell commands as context-menu items.
  // In order to show the proper type (hide/show) we check for the CSS classes
  // to exist (or not exist) on the cell. Note that there is no space between
  // .jp-Cell and the next selector, this is required so that a single
  // element must match both selectors.
  jl.app.contextMenu.addItem({
    command: "notebook:hide-cell-in-viewer",
    selector: `.jp-Cell:not(.${CLASS_NAME_COLLAPSED})`,
    rank: 9,
  });
  jl.app.contextMenu.addItem({
    command: "notebook:show-cell-in-viewer",
    selector: `.jp-Cell.${CLASS_NAME_COLLAPSED}`,
    rank: 9,
  });
  jl.app.contextMenu.addItem({
    command: "notebook:hide-output-cell-in-viewer",
    selector: `.jp-Cell:not(.${CLASS_NAME_COLLAPSED_OUTPUT})`,
    rank: 10,
  });
  jl.app.contextMenu.addItem({
    command: "notebook:show-output-cell-in-viewer",
    selector: `.jp-Cell.${CLASS_NAME_COLLAPSED_OUTPUT}`,
    rank: 10,
  });

  // Add Notebook Cell Type change items to the right-click context menu.
  // The "selector" field determines if an item is visible based on the css class
  // of the click target, this is how we control which change option to show depending
  // on the current type of the cell.
  jl.app.contextMenu.addItem({
    command: "notebook:change-cell-to-markdown",
    selector: ".jp-Notebook .jp-CodeCell",
    rank: 9,
  });
  jl.app.contextMenu.addItem({
    command: "notebook:change-cell-to-code",
    selector: ".jp-Notebook .jp-MarkdownCell",
    rank: 9,
  });

  function activeCellListener(handler: (nb: Notebook) => () => void) {
    withNotebook(nb => {
      let unsub = handler(nb);
      nb.activeCellChanged.connect(() => {
        unsub();
        unsub = handler(nb);
      });
    });
  }

  return {
    logAnalyticsEvent,
    isQueued,
    onCellQueueChange,
    clearCellQueue,
    runCommand,
    onSelectionChange(selected: () => void) {
      activeCellListener(nb => {
        if (nb.activeCellIndex < 0) {
          return () => {};
        }
        const cell = nb.widgets[nb.activeCellIndex];
        const editor = cell.editor as CodeMirrorEditor;

        let lastValue: boolean | null = null; // Only trigger selected() when value changes.
        const trigger = () => {
          const value = editor.doc.getSelection() === "";
          if (lastValue === value) {
            return;
          }
          lastValue = value;
          selected();
        };
        editor.model.selections.changed.connect(trigger);
        return () => editor.model.selections.changed.disconnect(trigger);
      });
    },
    onCellStateChange(onchange: (cells: CellInfo[]) => void) {
      withNotebook(nb => {
        const updateCellStates = () => {
          const cells = nb.widgets.map(cell => ({
            isSelected: nb.isSelectedOrActive(cell),
            cellType: cell.model.type,
            isHidden: !!cell.model.metadata.get(CLASS_NAME_COLLAPSED),
            isOutputHidden: !!cell.model.metadata.get(
              CLASS_NAME_COLLAPSED_OUTPUT
            ),
          }));
          normalizeClasses();
          onchange(cells);
        };
        nb.activeCellChanged.connect(updateCellStates);
        nb.selectionChanged.connect(updateCellStates);
        cellHiddenSignal.connect(updateCellStates);
        updateCellStates();
      });
    },
    undeleteCell() {
      withNotebook(nb => {
        // JupyterLab doesn't support a direct 'undo last cell delete', so instead
        // we will walk through the undo stack and undo the last cell removal we find.
        const list = nb.model?.cells as ObservableUndoableList<ICellModel>;
        // Get the undoable list private field:
        const li = (list as any)._cellOrder;
        // Get the stack of undo/redo actions from first action up to current position in the stack.
        const stack = (li._stack as any[]).slice(0, li._index + 1);
        // Walk backwards through undo stack:
        for (const changes of stack.reverse()) {
          // Check changes in the stack:
          for (const change of changes) {
            // Undo the first remove we find:
            if (change.type === "remove") {
              li._undoChange(change);
              return;
            }
          }
        }
      });
    },
    insertCellAbove(type: CellType, index: number | null = null) {
      return withActive(index, nb => {
        NotebookActions.insertAbove(nb);
        NotebookActions.changeCellType(nb, type);
      });
    },
    insertCellBelow(type: CellType, index: number | null = null) {
      return withActive(index, nb => {
        NotebookActions.insertBelow(nb);
        NotebookActions.changeCellType(nb, type);
      });
    },
    async setSource(content: string) {
      withNotebook(nb => nb.model?.fromJSON(JSON.parse(content)));
      clearReadOnly();
      normalizeClasses();
      // Force notebook to record cell execution timing so that cell metadata
      // has the 'execution' field.
      jl.notebookTracker.currentWidget!.content.notebookConfig.recordTiming =
        true;
      // Markdown toolbar is singleton so by default all markdown must be rendered.
      await runCommand("notebook:render-all-markdown");
    },
    getSourceWithOutputs() {
      return withNotebook(nb => nb.model?.toString()) || "";
    },
    getSource() {
      return (
        withNotebook(nb => {
          if (!nb.model) {
            return;
          }
          const restore: (() => void)[] = [];
          for (let i = 0; i < nb.model.cells.length; i++) {
            const cell = nb.model.cells.get(i);
            if (cell.type === "code") {
              const code = cell as CodeCellModel;
              if (code) {
                // Make the output cells convert to empty JSON so they are not included
                // in the source, restore them after we generate the source json.
                const backup = code.outputs.toJSON;
                restore.push(() => (code.outputs.toJSON = backup));
                code.outputs.toJSON = () => [];
              }
            }
          }
          const result = nb.model.toString();
          restore.forEach(fn => fn());
          return result;
        }) || ""
      );
    },
    deleteCell(index: number | null = null) {
      return runCommand("notebook:delete-cell", index);
    },
    toCode(index: number | null = null) {
      return runCommand("notebook:change-cell-to-code", index);
    },
    async toMarkdown(index: number | null = null) {
      await runCommand("notebook:change-cell-to-markdown", index);
    },
    collapseOutput(index: number) {
      return runCommand("notebook:hide-cell-outputs", index);
    },
    expandOutput(index: number) {
      return runCommand("notebook:show-cell-outputs", index);
    },
    executeCell(index: number) {
      runCellsOffline([index]);
      return runCommand("notebook:run-cell", index);
    },
    executeActiveCell() {
      runCellsOffline([currentCellIndex()]);
      return runCommand("notebook:run-cell");
    },
    cutCell() {
      return runCommand("notebook:cut-cell");
    },
    copyCell() {
      return runCommand("notebook:copy-cell");
    },
    pasteCell() {
      return runCommand("notebook:paste-cell-below");
    },
    getActiveSelection() {
      return (
        withNotebook(nb => {
          const cell = nb.activeCell as CodeCell;
          if (!cell) {
            return;
          }
          const editor = cell.editor as CodeMirrorEditor;
          const selected = editor.doc.getSelection() as string | null;
          return selected;
        }) || ""
      );
    },
    executeActiveSelectionOrCurrentLine(cellIndex = 0) {
      return runCommand("notebook:execute-selected-code", cellIndex);
    },
    executeActiveSelection() {
      return runCommand("notebook:execute-active-selection");
    },
    executeAllCells() {
      runCellsOffline(range(0, numCells()));
      return runCommand("notebook:run-all-cells");
    },
    getCellFrozenState(index: number) {
      return (
        withNotebook(nb => {
          const isFrozen = nb.model?.cells
            .get(index)
            .metadata.get(KaggleCellMetadataKey.KAGGLE_FREEZE);
          return isFrozen === true;
        }) || false
      );
    },
    setCellFrozenState(index: number, isFrozen: boolean) {
      withNotebook(nb => {
        nb.model?.cells
          .get(index)
          .metadata.set(KaggleCellMetadataKey.KAGGLE_FREEZE, isFrozen);
      });
    },
    setAutosaveInterval(interval: number) {
      // TODO(herbison): This is only used to set it to 0 presumably
      // to disable it, consider doing this instead:
      // jl.documentManager.autosave = false;
      jl.documentManager.autosaveInterval = interval;
    },
    stopExecution() {
      return runCommand("notebook:interrupt-kernel");
    },
    restartKernel() {
      // Note that the commands for doing this trigger dialogs, that's
      // why we are not using them.
      withNotebook((nb, ctx) => {
        NotebookActions.clearAllOutputs(nb);
        ctx.restartKernel();
      });
    },
    moveActiveCell(direction: VerticalDirection) {
      if (direction === VerticalDirection.UP) {
        return runCommand("notebook:move-cell-up");
      } else {
        return runCommand("notebook:move-cell-down");
      }
    },
    ensureEditMode(index: number) {
      return withNotebook(nb => {
        nb.activeCellIndex = index;
        nb.mode = "edit";
      });
    },
    execute(code: string) {
      // TODO(herbison): One observed problem is that focus is returned to a cell after running something in the console.
      const executeCode = () =>
        withNotebook((nb, ctx) => {
          const model = nb.model;
          if (!model) {
            return;
          }

          // WARNING: This is a bit hacky because there doesn't seem to be
          // a simple way to execute arbitrary code. One better solution
          // might be to use JupyterLabs console instead of implementing
          // our own.

          // Create a code cell:
          const cell = model.contentFactory.createCell("code", {});

          // Insert the new cell at the end with the given code in it:
          const last = model.cells.length;
          model.cells.insert(last, cell);
          cell.value.text = code;
          const codeCell = nb.widgets[last] as CodeCell;

          // TODO(herbison): Make this a command?
          // Execute the cell to execute the given code:
          CodeCell.execute(codeCell, ctx);

          // Immediately delete the cell to hide how we executed this code.
          model.cells.remove(last);
        });
      return runOffline(executeCode);
    },
    toggleShowLineNumbers() {
      return runCommand("notebook:toggle-all-cell-line-numbers");
    },
    collapseAllCodeCells() {
      return runCommand("notebook:hide-all-cell-code");
    },
    expandAllCodeCells() {
      return runCommand("notebook:show-all-cell-code");
    },
    toggleOutputScrolling() {
      return withNotebook(nb => {
        const cell = nb.widgets[nb.activeCellIndex] as CodeCell;
        if (!cell) {
          return;
        }
        if (!cell.outputsScrolled) {
          return runCommand("notebook:enable-output-scrolling");
        } else {
          return runCommand("notebook:disable-output-scrolling");
        }
      });
    },
    toggleWordWrap() {
      return withNotebook(nb => {
        nb.widgets.forEach(cell => {
          const editor = cell.editor as CodeMirrorEditor;
          editor.setOption(
            "lineWrap",
            editor.getOption("lineWrap") === "on" ? "off" : "on"
          );
        });
      });
    },
    runAllAbove() {
      runCellsOffline(range(0, currentCellIndex()));
      return runCommand("notebook:run-all-above");
    },
    runAllBelow() {
      runCellsOffline(range(currentCellIndex() + 1, numCells()));
      return runCommand("notebook:run-all-below");
    },
    navigateToCode(cl: CodeLocation) {
      const matches = cl.cellId.match(/ipython-input-([0-9]+)-.*/);
      const inputPromptNumber =
        matches && matches.length === 2 ? Number(matches[1]) : null;
      if (inputPromptNumber === null) {
        return;
      }
      return withNotebook(nb => {
        for (const widget of nb.widgets) {
          const cell = widget as CodeCell;
          if (!cell) {
            continue;
          }
          // Find the code cell which was executed:
          if (cell.model.executionCount !== inputPromptNumber) {
            continue;
          }
          const editor = cell.editor as CodeMirrorEditor;

          // Jump to the CodeLocation in the editor:
          const anchor = { line: cl.lineNumber - 1, column: 0 };
          editor.revealPosition(anchor);

          // Select the line of the CodeLocation in the editor:
          editor.setSelection({
            start: anchor,
            end: { ...anchor, column: 200 },
          });
        }
      });
    },
  };
};
